import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CircularProgress } from '@mui/material';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import Loader from '../../components/Loader';
import { AdvertisePackage } from '../../client/services';
import { purchaseCredits, getPurchaseHistory, checkPaymentStatus, getFreePackageStatus, PurchaseHistoryItem } from '../../services/company-credits';
import { toastHandler } from '../../utils/toastHandler';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const purchaseSchema = Yup.object().shape({
  packageId: Yup.string().required('Package selection is required'),
});

const PurchaseCredits = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState<any[]>([]);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<any | null>(null);
  const [purchaseHistory, setPurchaseHistory] = useState<PurchaseHistoryItem[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [pendingPaymentId, setPendingPaymentId] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>('');
  const [refreshingPaymentId, setRefreshingPaymentId] = useState<string | null>(null);
  const [purchasedFreePackageIds, setPurchasedFreePackageIds] = useState<string[]>([]);

  // Fetch available packages
  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await AdvertisePackage.find({
        query: {
          limit: 100,
          type: 'company',
        },
      });
      // Filter only credit-based packages
      const creditPackages = response.data.filter(
        (pkg: any) => pkg.packageType === 'credit_based' && pkg.activeState
      );
      // Sort packages by price (least to highest)
      const sortedPackages = creditPackages.sort((a: any, b: any) => {
        const priceA = parseFloat(String(a.price || 0));
        const priceB = parseFloat(String(b.price || 0));
        return priceA - priceB;
      });
      setPackages(sortedPackages);
    } catch (error: any) {
      console.error('Error fetching packages:', error);
      toastHandler(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch purchase history
  const fetchPurchaseHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await getPurchaseHistory({
        page: historyPage + 1, // Backend uses 1-based page
        limit: historyLimit,
        status: filterStatus || undefined,
      });
      setPurchaseHistory(response.data || []);
      setHistoryTotal(response.total || 0);
    } catch (error: any) {
      console.error('Error fetching purchase history:', error);
      toastHandler(error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch free package purchase status
  const fetchFreePackageStatus = async () => {
    try {
      const response = await getFreePackageStatus();
      setPurchasedFreePackageIds(response.purchasedPackageIds || []);
    } catch (error: any) {
      console.error('Error fetching free package status:', error);
      // Don't show error to user, just default to empty array
      setPurchasedFreePackageIds([]);
    }
  };

  // Handle payment redirect from SkipCash
  useEffect(() => {
    const paymentId = searchParams.get('paymentId');
    const status = searchParams.get('status');
    
    if (paymentId && status === 'success') {
      console.log('[Purchase Credits] Payment redirect detected - paymentId:', paymentId);
      
      // Remove URL parameters to clean up the URL
      setSearchParams({});
      
      // Check payment status
      const checkRedirectedPayment = async () => {
        try {
          const result = await checkPaymentStatus(paymentId);
          const currentStatus = result.paymentStatus || 'PENDING';
          const normalizedStatus = currentStatus.toUpperCase();
          
          if (normalizedStatus === 'PAID') {
            toast.success('Payment completed successfully! Credits have been added to your account.');
            // Refresh data to show updated credits
            fetchPurchaseHistory();
            fetchPackages();
            fetchFreePackageStatus();
          } else if (normalizedStatus === 'FAILED' || normalizedStatus === 'CANCELLED') {
            toast.error(`Payment ${normalizedStatus === 'FAILED' ? 'failed' : 'was cancelled'}. No credits were added.`);
            fetchPurchaseHistory();
          } else if (normalizedStatus === 'PENDING') {
            // Payment still pending - start polling
            setPendingPaymentId(paymentId);
            setPaymentStatus('PENDING');
            toast.info('Payment is still processing. Please wait...');
          }
        } catch (error: any) {
          console.error('[Purchase Credits] Error checking payment status after redirect:', error);
          toast.error('Could not verify payment status. Please check your purchase history.');
          fetchPurchaseHistory();
        }
      };
      
      checkRedirectedPayment();
    }
  }, [searchParams]);

  useEffect(() => {
    fetchPackages();
    fetchPurchaseHistory();
    fetchFreePackageStatus();
  }, []);

  useEffect(() => {
    fetchPurchaseHistory();
  }, [historyPage, historyLimit, filterStatus]);

  // Poll payment status if there's a pending payment
  useEffect(() => {
    if (!pendingPaymentId) return;

    let interval: NodeJS.Timeout | null = null;
    let timeout: NodeJS.Timeout | null = null;

    const pollPaymentStatus = async () => {
      if (!pendingPaymentId) return;
      try {
        const result = await checkPaymentStatus(pendingPaymentId);
        const currentStatus = result.paymentStatus || 'PENDING';
        setPaymentStatus(currentStatus);

        // Map payment status (can be 'paid', 'pending', 'failed', 'cancelled', 'PAID', 'PENDING', etc.)
        const normalizedStatus = currentStatus.toUpperCase();
        
        if (normalizedStatus === 'PAID') {
          // Payment completed, refresh data
          toast.success('Payment completed successfully! Credits have been added.');
          setPendingPaymentId(null);
          setPaymentStatus('');
          fetchPurchaseHistory();
          // Refresh packages if needed
          fetchPackages();
          if (interval) clearInterval(interval);
          if (timeout) clearTimeout(timeout);
        } else if (normalizedStatus === 'FAILED' || normalizedStatus === 'CANCELLED') {
          // Payment failed or cancelled
          toast.error(`Payment ${normalizedStatus === 'FAILED' ? 'failed' : 'was cancelled'}. No credits were added. You can try purchasing again.`);
          setPendingPaymentId(null);
          setPaymentStatus('');
          fetchPurchaseHistory(); // Refresh to show updated status
          if (interval) clearInterval(interval);
          if (timeout) clearTimeout(timeout);
        } else if (normalizedStatus === 'PENDING') {
          // Still pending - continue polling
          // This handles the case where user closes payment window
          // Payment will remain PENDING until webhook updates it or timeout
        }
      } catch (error: any) {
        console.error('Error polling payment status:', error);
      }
    };

    // Poll immediately, then every 5 seconds
    pollPaymentStatus();
    interval = setInterval(pollPaymentStatus, 5000);
    
    // Stop polling after 10 minutes (payment should complete by then, or user closed window)
    timeout = setTimeout(() => {
      if (paymentStatus === 'PENDING') {
        // Payment still pending after timeout - user likely closed window or abandoned payment
        toast.warning('Payment is still pending. If you closed the payment window, the payment will remain pending. You can check the status in Purchase History.');
      }
      setPendingPaymentId(null);
      setPaymentStatus('');
      fetchPurchaseHistory(); // Refresh to show current status
      if (interval) clearInterval(interval);
    }, 10 * 60 * 1000); // Increased to 10 minutes

    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [pendingPaymentId]);

  const purchaseFormik = useFormik({
    initialValues: {
      packageId: '',
    },
    validationSchema: purchaseSchema,
    onSubmit: async (values) => {
      try {
        const response = await purchaseCredits({
          packageId: values.packageId,
        });
        
        // Validate response has required fields
        if (!response) {
          toast.error('No response received from server. Please try again.');
          return;
        }

        if (!response.paymentId) {
          console.error('[Purchase Credits] Missing paymentId in response:', response);
          toast.error('Payment ID was not generated. Please try again or contact support.');
          return;
        }

        // Check if this is a free package (status = PAID, no payUrl needed)
        if (response.status === 'PAID' && !response.payUrl) {
          // Free package - credits already added, no payment needed
          toast.success(`Successfully purchased ${response.creditCount} credits!`);
          setPurchaseModalOpen(false);
          purchaseFormik.resetForm();
          // Refresh history immediately to show the purchase
          fetchPurchaseHistory();
          // Refresh free package status to update button to "Purchased"
          fetchFreePackageStatus();
          return;
        }

        // For paid packages, payUrl is required
        if (!response.payUrl) {
          console.error('[Purchase Credits] Missing payUrl in response:', response);
          toast.error('Payment URL was not generated. Please try again or contact support.');
          return;
        }
        
        // Store payment ID for status polling
        setPendingPaymentId(response.paymentId);
        setPaymentStatus('PENDING');
        
        // Open payment URL in new window
        try {
          const paymentWindow = window.open(response.payUrl, '_blank');
          
          // Check if popup was blocked
          if (!paymentWindow || paymentWindow.closed || typeof paymentWindow.closed === 'undefined') {
            toast.warning('Popup was blocked. Please allow popups for this site and try again, or click the payment link manually.');
            // Fallback: show the URL so user can copy it
            console.log('Payment URL:', response.payUrl);
          } else {
            toast.info('Redirecting to payment gateway... Please complete the payment.');
          }
        } catch (windowError: any) {
          console.error('[Purchase Credits] Error opening payment window:', windowError);
          toast.error('Could not open payment window. Please check your browser settings.');
          // Fallback: log the URL
          console.log('Payment URL:', response.payUrl);
        }
        
        setPurchaseModalOpen(false);
        purchaseFormik.resetForm();
        
        // Refresh history immediately to show pending payment
        fetchPurchaseHistory();
        fetchFreePackageStatus();
      } catch (error: any) {
        console.error('[Purchase Credits] Purchase error:', error);
        toastHandler(error);
      }
    },
  });

  const handlePurchaseClick = (pkg: any) => {
    setSelectedPackage(pkg);
    purchaseFormik.setFieldValue('packageId', pkg.id);
    setPurchaseModalOpen(true);
  };

  const handleHistoryPageChange = (_event: unknown, newPage: number) => {
    setHistoryPage(newPage);
  };

  const handleHistoryRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHistoryLimit(parseInt(event.target.value, 10));
    setHistoryPage(0);
  };

  const getStatusColor = (status: string): 'default' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'COMPLETED':
      case 'paid':
        return 'success';
      case 'PENDING':
      case 'pending':
        return 'warning';
      case 'FAILED':
      case 'CANCELLED':
      case 'failed':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleRefreshPaymentStatus = async (paymentId: string) => {
    try {
      setRefreshingPaymentId(paymentId);
      const result = await checkPaymentStatus(paymentId);
      
      if (result.success) {
        toast.success(result.message || 'Payment status updated');
        // Refresh purchase history to show updated status
        await fetchPurchaseHistory();
      }
    } catch (error: any) {
      // Error already handled by toastHandler in service
    } finally {
      setRefreshingPaymentId(null);
    }
  };

  if (loading) {
    return (
      <Page title="Purchase Credits">
        <Container>
          <Loader />
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Purchase Credits">
      <Container maxWidth="xl">
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4">Purchase Credits</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Select a credit package to purchase
            </Typography>
          </Box>
          {pendingPaymentId && (
            <Alert 
              severity={
                paymentStatus?.toUpperCase() === 'PAID' ? 'success' : 
                paymentStatus?.toUpperCase() === 'FAILED' || paymentStatus?.toUpperCase() === 'CANCELLED' ? 'error' : 
                'info'
              }
              sx={{ minWidth: 300 }}
            >
              Payment Status: {paymentStatus}
              {(paymentStatus?.toUpperCase() === 'PENDING' || paymentStatus?.toUpperCase() === 'pending') && ' (Waiting for payment completion...)'}
            </Alert>
          )}
        </Box>

        {/* Available Packages */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {packages.length > 0 ? (
            packages.map((pkg) => (
              <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" gutterBottom>
                      {pkg.labelEn}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {pkg.labelAr}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h4" color="primary" gutterBottom>
                        {pkg.creditCount} Credits
                      </Typography>
                      <Typography variant="h6" color="text.secondary">
                        {pkg.price === 0 || pkg.price === '0' ? 'Free' : `QAR ${pkg.price}`}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Credit Expiration: {pkg.creditExpirationDays} days
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Ad Duration: {pkg.duration} days
                      </Typography>
                    </Box>
                    {(() => {
                      // Check if this is a free package (price = 0)
                      const isFreePackage = pkg.price === 0 || pkg.price === '0' || parseFloat(String(pkg.price || 0)) === 0;
                      // Check if this free package has been purchased
                      const isPurchased = isFreePackage && purchasedFreePackageIds.includes(pkg.id);
                      
                      return (
                        <Button
                          fullWidth
                          variant={isPurchased ? "outlined" : "contained"}
                          size="large"
                          onClick={() => handlePurchaseClick(pkg)}
                          disabled={isPurchased}
                          startIcon={isPurchased ? <Iconify icon="eva:checkmark-circle-2-fill" /> : <Iconify icon="eva:shopping-cart-fill" />}
                          sx={isPurchased ? {
                            color: 'success.main',
                            borderColor: 'success.main',
                            '&:hover': {
                              borderColor: 'success.dark',
                              backgroundColor: 'success.light',
                            },
                          } : {}}
                        >
                          {isPurchased ? 'Purchased' : 'Purchase'}
                        </Button>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">No credit packages available at the moment.</Alert>
            </Grid>
          )}
        </Grid>

        {/* Purchase History */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Purchase History</Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filter Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                  {/* <MenuItem value="CANCELLED">Cancelled</MenuItem> */}
                </Select>
              </FormControl>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {loadingHistory ? (
              <Loader />
            ) : purchaseHistory.length > 0 ? (
              <>
                <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 1000 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Package Name</TableCell>
                        <TableCell align="right">Credits</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Purchase Date</TableCell>
                        <TableCell>Expires At</TableCell>
                        <TableCell>Expired</TableCell>
                        <TableCell align="right">Used</TableCell>
                        <TableCell align="right">Remaining</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseHistory
                        .map((item) => {
                          const displayStatus = (item.paymentStatus?.toUpperCase() === 'PAID' || item.paymentStatus?.toUpperCase() === 'COMPLETED') ? 'COMPLETED' : 'FAILED';
                          return (
                            <TableRow key={item.id} hover>
                              <TableCell>{item.packageName}</TableCell>
                              <TableCell align="right">{item.creditCount}</TableCell>
                              <TableCell align="right">QAR {item.price}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    label={displayStatus}
                                    color={getStatusColor(displayStatus)}
                                    size="small"
                                  />
                                </Box>
                              </TableCell>
                          <TableCell>
                            {moment(item.purchasedAt).format('YYYY-MM-DD HH:mm')}
                          </TableCell>
                          <TableCell>
                            {item.expiresAt
                              ? moment(item.expiresAt).format('YYYY-MM-DD')
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.isExpired ? 'Yes' : 'No'}
                              color={item.isExpired ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">{item.usedCredits || 0}</TableCell>
                          <TableCell align="right">{item.remainingCredits || 0}</TableCell>
                          <TableCell align="center">
                            {(item.paymentStatus === 'PENDING' || item.paymentStatus === 'pending') && item.paymentId && (
                              <Tooltip title="Check payment status from SkipCash">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRefreshPaymentStatus(item.paymentId)}
                                  disabled={refreshingPaymentId === item.paymentId}
                                >
                                  {refreshingPaymentId === item.paymentId ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Iconify icon="eva:refresh-fill" width={18} height={18} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={historyTotal}
                  rowsPerPage={historyLimit}
                  page={historyPage}
                  onPageChange={handleHistoryPageChange}
                  onRowsPerPageChange={handleHistoryRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </>
            ) : (
              <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                No purchase history
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Purchase Confirmation Modal */}
        <Dialog
          open={purchaseModalOpen}
          onClose={() => {
            setPurchaseModalOpen(false);
            purchaseFormik.resetForm();
            setSelectedPackage(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <form onSubmit={purchaseFormik.handleSubmit}>
            <DialogTitle>Confirm Purchase</DialogTitle>
            <DialogContent>
              {selectedPackage && (
                <Box sx={{ pt: 2 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You are about to purchase:
                  </Alert>
                  <Typography variant="h6" gutterBottom>
                    {selectedPackage.labelEn}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {selectedPackage.labelAr}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Credits
                      </Typography>
                      <Typography variant="h6">{selectedPackage.creditCount}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Price
                      </Typography>
                      <Typography variant="h6">
                        {selectedPackage.price === 0 || selectedPackage.price === '0'
                          ? 'Free'
                          : `QAR ${selectedPackage.price}`}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Credit Expiration
                      </Typography>
                      <Typography variant="body2">
                        {selectedPackage.creditExpirationDays} days
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Ad Duration
                      </Typography>
                      <Typography variant="body2">{selectedPackage.duration} days</Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setPurchaseModalOpen(false);
                  purchaseFormik.resetForm();
                  setSelectedPackage(null);
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={purchaseFormik.isSubmitting}
              >
                Proceed to Payment
              </LoadingButton>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Page>
  );
};

export default PurchaseCredits;

